import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getDatabaseConfiguration,
  updateDatabaseConfiguration,
  testDatabaseConnection,
  migrateDataToOracle,
} from "../api/adminApi";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { Hero } from "../components/adminSurface/AdminSurface";
import { StackArt } from "../components/adminSurface/heroArt";

export default function DatabaseConfiguration() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [config, setConfig] = useState({
    provider: "SqlServer",
    sqlServerConnectionString: "",
    oracleConnectionString: "",
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetchConfiguration();
  }, [token]);

  const fetchConfiguration = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getDatabaseConfiguration(token);
      if (data) {
        setConfig(data);
        setOriginalConfig(data);
      }
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    setTestResult(null);
    try {
      await updateDatabaseConfiguration(token, config);
      setSuccess(
        "Database configuration saved. Restart the application to apply the new provider."
      );
      setOriginalConfig(config);
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError("");
    try {
      const result = await testDatabaseConnection(token, config);
      setTestResult(result);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm(
      "This will copy ALL data from SQL Server into the Oracle database.\n\n" +
      "Existing Oracle data for each table will remain (new rows are appended).\n\n" +
      "Continue?"
    )) return;
    setMigrating(true);
    setMigrationResult(null);
    setError("");
    try {
      const result = await migrateDataToOracle(token);
      setMigrationResult(result);
    } catch (err) {
      setError("Migration failed: " + err.message);
    } finally {
      setMigrating(false);
    }
  };

  const hasChanges =
    JSON.stringify(config) !== JSON.stringify(originalConfig);

  const activeConnStr =
    config.provider === "Oracle"
      ? config.oracleConnectionString
      : config.sqlServerConnectionString;
  const hasConnStr = activeConnStr && activeConnStr.trim().length > 0;

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />
      <div className="page-container">
        <Hero
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
              strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
              <ellipse cx="12" cy="5" rx="8" ry="3" />
              <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
              <path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
            </svg>
          }
          title={t("database.configuration", { defaultValue: "Database Provider" })}
          subtitle={t("database.subtitle", {
            defaultValue:
              "Select the relational database backend for the application. Changes take effect after a restart.",
          })}
          art={<StackArt />}
        />

        {/* Restart-required banner */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 16px",
            backgroundColor: "#fef9c3",
            border: "1px solid #fde047",
            borderRadius: 8,
            marginBottom: 24,
            fontSize: 13,
            color: "#713f12",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          Saved changes will be applied on the next application restart.
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#666" }}>
            <div
              style={{
                width: 32,
                height: 32,
                border: "3px solid #e0e0e0",
                borderTop: "3px solid #2563eb",
                borderRadius: "50%",
                display: "inline-block",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ marginTop: 12 }}>Loading configuration...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Provider Selection Card */}
            <div className="config-card" style={{ marginBottom: 24 }}>
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                  Database Provider
                </h2>
              </div>
              <div style={{ padding: 24 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 14,
                    fontWeight: 500,
                    marginBottom: 12,
                  }}
                >
                  Select Database Backend
                </label>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 16,
                  }}
                >
                  {/* SQL Server */}
                  <div
                    onClick={() =>
                      setConfig({ ...config, provider: "SqlServer" })
                    }
                    style={{
                      padding: 16,
                      border:
                        config.provider === "SqlServer"
                          ? "2px solid #3b82f6"
                          : "2px solid #d1d5db",
                      borderRadius: 8,
                      cursor: "pointer",
                      backgroundColor:
                        config.provider === "SqlServer" ? "#eff6ff" : "#fff",
                      transition: "all 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="radio"
                        checked={config.provider === "SqlServer"}
                        onChange={() =>
                          setConfig({ ...config, provider: "SqlServer" })
                        }
                        style={{ marginTop: 2 }}
                      />
                      <div>
                        <h3
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            marginBottom: 6,
                          }}
                        >
                          Microsoft SQL Server
                        </h3>
                        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                          Default provider — Azure SQL or on-premises SQL Server
                        </p>
                        <div
                          style={{ display: "flex", gap: 8, marginTop: 10 }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              backgroundColor: "#d1fae5",
                              color: "#065f46",
                              borderRadius: 4,
                            }}
                          >
                            Default
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                              borderRadius: 4,
                            }}
                          >
                            Azure Ready
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Oracle */}
                  <div
                    onClick={() =>
                      setConfig({ ...config, provider: "Oracle" })
                    }
                    style={{
                      padding: 16,
                      border:
                        config.provider === "Oracle"
                          ? "2px solid #f97316"
                          : "2px solid #d1d5db",
                      borderRadius: 8,
                      cursor: "pointer",
                      backgroundColor:
                        config.provider === "Oracle" ? "#fff7ed" : "#fff",
                      transition: "all 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", gap: 12 }}>
                      <input
                        type="radio"
                        checked={config.provider === "Oracle"}
                        onChange={() =>
                          setConfig({ ...config, provider: "Oracle" })
                        }
                        style={{ marginTop: 2 }}
                      />
                      <div>
                        <h3
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            marginBottom: 6,
                          }}
                        >
                          Oracle Database
                        </h3>
                        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                          {t("database.oracleDescription", {
                            defaultValue:
                              "Oracle 23ai / 19c — Enterprise, Standard, Express or Free editions",
                          })}
                        </p>
                        <div
                          style={{ display: "flex", gap: 8, marginTop: 10 }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              backgroundColor: "#ffedd5",
                              color: "#9a3412",
                              borderRadius: 4,
                            }}
                          >
                            {t("database.badgeOnPremises", {
                              defaultValue: "On-Premises",
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Connection String Card */}
            <div className="config-card" style={{ marginBottom: 24 }}>
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                  {config.provider === "Oracle"
                    ? "Oracle Connection String"
                    : "SQL Server Connection String"}
                </h2>
              </div>
              <div style={{ padding: 24 }}>
                {config.provider === "SqlServer" && (
                  <>
                    <p
                      style={{
                        fontSize: 13,
                        color: "#666",
                        marginBottom: 12,
                      }}
                    >
                      Format:{" "}
                      <code style={{ backgroundColor: "#f3f4f6", padding: "2px 4px", borderRadius: 3 }}>
                        Server=host;Database=DEPOSITORY;User ID=sa;Password=...;TrustServerCertificate=true;
                      </code>
                    </p>
                    <textarea
                      value={config.sqlServerConnectionString}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          sqlServerConnectionString: e.target.value,
                        })
                      }
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontFamily: "monospace",
                        fontSize: 13,
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                      placeholder="Server=localhost;Database=DEPOSITORY;User ID=sa;Password=...;TrustServerCertificate=true;"
                    />
                  </>
                )}

                {config.provider === "Oracle" && (
                  <>
                    <p
                      style={{
                        fontSize: 13,
                        color: "#666",
                        marginBottom: 8,
                      }}
                    >
                      {t("database.oracleFormat", { defaultValue: "Format:" })}{" "}
                      <code style={{ backgroundColor: "#f3f4f6", padding: "2px 4px", borderRadius: 3 }}>
                        Data Source=host:1521/FREEPDB1;User Id=DEPOSITORY;Password=...;
                      </code>
                    </p>
                    <p
                      style={{
                        fontSize: 12.5,
                        color: "#666",
                        marginBottom: 8,
                        lineHeight: 1.5,
                      }}
                    >
                      {t("database.oracleTlsHint", {
                        defaultValue:
                          "Oracle Cloud / Autonomous Database: use a full TLS connect descriptor as the Data Source. For mutual TLS (mTLS), append TNS_ADMIN=<wallet-dir>; pointing to the unzipped wallet directory.",
                      })}
                    </p>
                    <p style={{ marginBottom: 12 }}>
                      <code
                        style={{
                          display: "block",
                          backgroundColor: "#f3f4f6",
                          padding: "8px 10px",
                          borderRadius: 4,
                          fontSize: 11.5,
                          color: "#374151",
                          overflowX: "auto",
                          whiteSpace: "pre",
                        }}
                      >
                        {"Data Source=(description=(address=(protocol=tcps)(host=adb.region.oraclecloud.com)(port=1522))" +
                          "(connect_data=(service_name=abcd_high.adb.oraclecloud.com))(security=(ssl_server_dn_match=yes)));\n" +
                          "User Id=ADMIN;Password=...;TNS_ADMIN=/opt/depository/wallet;"}
                      </code>
                    </p>
                    <textarea
                      value={config.oracleConnectionString}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          oracleConnectionString: e.target.value,
                        })
                      }
                      rows={4}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontFamily: "monospace",
                        fontSize: 13,
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                      placeholder="Data Source=localhost:1521/FREEPDB1;User Id=DEPOSITORY;Password=DepositoryApp2024!;"
                    />
                  </>
                )}

                {/* Test Result */}
                {testResult && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: "12px 16px",
                      backgroundColor: testResult.success ? "#f0fdf4" : "#fef2f2",
                      border: `1px solid ${testResult.success ? "#bbf7d0" : "#fecaca"}`,
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    {testResult.success ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#16a34a"
                        strokeWidth="2"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#dc2626"
                        strokeWidth="2"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                    )}
                    <div style={{ flex: 1 }}>
                      {testResult.success && testResult.details?.edition ? (
                        <>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#15803d",
                              marginBottom: 2,
                            }}
                          >
                            {t("database.detectedEdition", {
                              defaultValue: "Detected: {{edition}}",
                              edition: testResult.details.edition,
                            })}
                          </div>
                          {testResult.details.banner && (
                            <div
                              style={{
                                fontSize: 12,
                                color: "#4b5563",
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, monospace",
                                wordBreak: "break-word",
                              }}
                              title={testResult.details.banner}
                            >
                              {testResult.details.banner}
                            </div>
                          )}
                          {testResult.details.productVersion && (
                            <div
                              style={{
                                fontSize: 12,
                                color: "#4b5563",
                              }}
                            >
                              {t("database.productVersion", {
                                defaultValue: "Version {{version}}",
                                version: testResult.details.productVersion,
                              })}
                              {testResult.details.productLevel
                                ? ` (${testResult.details.productLevel})`
                                : ""}
                            </div>
                          )}
                        </>
                      ) : (
                        <span
                          style={{
                            fontSize: 13,
                            color: testResult.success ? "#15803d" : "#b91c1c",
                          }}
                        >
                          {testResult.message}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Test button */}
                <div style={{ marginTop: 20 }}>
                  <button
                    onClick={handleTest}
                    disabled={testing || !hasConnStr}
                    className="secondary-btn"
                  >
                    {testing ? "Testing..." : "Test Connection"}
                  </button>
                </div>
              </div>
            </div>

            {/* Data Migration Card — only shown when Oracle is the active (saved) provider */}
            {originalConfig?.provider === "Oracle" && (
              <div className="config-card" style={{ marginBottom: 24 }}>
                <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb" }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                    Migrate Data from SQL Server
                  </h2>
                </div>
                <div style={{ padding: 24 }}>
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
                    Copy all existing rows from the SQL Server database into Oracle. Tables are
                    processed in foreign-key dependency order. Oracle identity sequences are reset
                    after each table so future inserts continue from the correct value.
                  </p>
                  <button
                    onClick={handleMigrate}
                    disabled={migrating}
                    style={{
                      padding: "9px 20px",
                      backgroundColor: migrating ? "#e5e7eb" : "#f97316",
                      color: migrating ? "#9ca3af" : "#fff",
                      border: "none",
                      borderRadius: 9,
                      cursor: migrating ? "not-allowed" : "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {migrating ? "Migrating… this may take a minute" : "Migrate Data from SQL Server"}
                  </button>

                  {/* Migration result report */}
                  {migrationResult && (
                    <div style={{ marginTop: 20 }}>
                      <div
                        style={{
                          padding: "10px 14px",
                          backgroundColor: migrationResult.overallSuccess ? "#f0fdf4" : "#fef2f2",
                          border: `1px solid ${migrationResult.overallSuccess ? "#bbf7d0" : "#fecaca"}`,
                          borderRadius: 6,
                          marginBottom: 12,
                          fontSize: 13,
                          fontWeight: 600,
                          color: migrationResult.overallSuccess ? "#15803d" : "#b91c1c",
                        }}
                      >
                        {migrationResult.overallSuccess ? "✓ " : "⚠ "}
                        {migrationResult.summary}
                      </div>

                      <div
                        style={{
                          maxHeight: 320,
                          overflowY: "auto",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                        }}
                      >
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                              <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Table</th>
                              <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 600 }}>Rows</th>
                              <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {migrationResult.tables?.map((t) => (
                              <tr
                                key={t.tableName}
                                style={{
                                  borderBottom: "1px solid #f3f4f6",
                                  backgroundColor: t.success ? "transparent" : "#fef2f2",
                                }}
                              >
                                <td style={{ padding: "6px 12px", fontFamily: "monospace" }}>{t.tableName}</td>
                                <td style={{ padding: "6px 12px", textAlign: "right" }}>
                                  {t.rowsCopied.toLocaleString()}
                                </td>
                                <td
                                  style={{
                                    padding: "6px 12px",
                                    color: t.success ? "#16a34a" : "#b91c1c",
                                    fontSize: 12,
                                  }}
                                >
                                  {t.success ? "OK" : t.error}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error / Success banners */}
            {error && (
              <div
                style={{
                  padding: "12px 16px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  color: "#b91c1c",
                  fontSize: 14,
                  marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}
            {success && (
              <div
                style={{
                  padding: "12px 16px",
                  backgroundColor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 6,
                  color: "#15803d",
                  fontSize: 14,
                  marginBottom: 16,
                }}
              >
                {success}
              </div>
            )}

            {/* Save button */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="primary-btn"
              >
                {saving ? "Saving..." : "Save Configuration"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
